import {Box, Button, FormControl, InputLabel, MenuItem, Select, TextField} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {fullManualArticle} from "../../services/ArticleService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getPrompts} from "../../services/PromptsService";

const ArticlePlayground = ({setLoading, companies, type}) => {

  const [data, setData] = useState({});
  const inputFileRef = useRef(null);
  const [imageTemplate, setImageTemplate] = useState({});
  const {notificationHandler} = useMessageContext();

  const [selectedFormat, setSelectedFormat] = useState("");
  const [selectedSubformat, setSelectedSubformat] = useState("");
  const [selectedFont, setSelectedFont] = useState("Inter-Bold");

  // New state for formats and subformats from server
  const [formats, setFormats] = useState([]);
  const [subformats, setSubformats] = useState([]);
  const [loadingFormats, setLoadingFormats] = useState(false);
  const [loadingSubformats, setLoadingSubformats] = useState(false);

  const fonts = ["Inter-Bold",
    "Inter-Regular",
    "Helvetica-Bold",
    "Helvetica-Regular",
    "Lato-Bold",
    "Lato-Regular",
    "Montserrat-Bold",
    "Montserrat-Regular",
    "OpenSans-Bold",
    "OpenSans-Regular",
    "Poppins-Bold",
    "Poppins-Regular",
    "Roboto-Bold",
    "Roboto-Regular",
    "SourceSerif4-Bold",
    "SourceSerif4-Regular",
    "Ubuntu-Bold",
    "Ubuntu-Regular",
    "Manrope-Bold",
    "Manrope-Regular",
    "NotoSans-Bold",
    "NotoSans-Regular",]

  // Fetch formats when company is selected
  useEffect(() => {
    if (data.company?.id) {
      setLoadingFormats(true);
      // Replace this with your actual API call
      fetchFormats(data.company.id, type === "ARTICLE" ? "ARTICLES" : "SINGLE_POST")
          .then(response => {
            setFormats(response);
            // Reset format and subformat when company changes
            setSelectedFormat("");
            setSelectedSubformat("");
            setSubformats([]);
          })
          .catch(error => {
            console.error('Error fetching formats:', error);
            notificationHandler.error('Failed to fetch formats');
          })
          .finally(() => setLoadingFormats(false));
    } else {
      setFormats([]);
      setSelectedFormat("");
      setSelectedSubformat("");
      setSubformats([]);
    }
  }, [data.company]);

  // Fetch subformats when format is selected
  useEffect(() => {
    if (selectedFormat && data.company?.id) {
      setLoadingSubformats(true);
      // Replace this with your actual API call
      fetchSubformats(data.company.id, selectedFormat)
          .then(response => {
            setSubformats(response);
            // Reset subformat when format changes
            setSelectedSubformat("");
          })
          .catch(error => {
            console.error('Error fetching subformats:', error);
            notificationHandler.error('Failed to fetch subformats');
          })
          .finally(() => setLoadingSubformats(false));
    } else {
      setSubformats([]);
      setSelectedSubformat("");
    }
  }, [selectedFormat, data.company]);

  // You'll need to implement these API functions
  const fetchFormats = async (companyId, category) => {
    const res = await getPrompts({
      companyId: companyId,
        category: category,
        type: "FORMAT"

    });
    return res.data;
  };

  const fetchSubformats = async (companyId, format) => {
    const res = await getPrompts({formatId: format.id, companyId: companyId});
    return res.data;
  };

  const handleSubmit = () => {
    setLoading(true);

    let body = {
      ...data,
      base64Image: imageTemplate.imageBase64,
      companyId: data.company?.id,
      type: type,
      font: selectedFont,
      formatId: selectedFormat.id,
      subFormatId: selectedSubformat.id
    };

    delete body.company;

    fullManualArticle(body)
        .then(() => setData({}))
        .then(() => setImageTemplate({}))
        .then(() => setSelectedFormat(""))
        .then(() => setSelectedSubformat(""))
        .then(() => notificationHandler.success('Manual Article triggered'))
        .finally(() => setLoading(false));
  };

  const onFileChange = async (e) => {
    /*Selected files data can be collected here.*/
    let body = await Promise.all([...e.target.files].map(async file => {
      return {
        name: file.name,
        imageBase64: await convertBase64(file)
      }
    }));

    setImageTemplate(body[0]);
  }

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        resolve(fileReader.result);
      }
      fileReader.onerror = (error) => {
        reject(error);
      }
    })
  }

  return (
      <Box sx={{display: "flex", alignItems: "center", flexDirection:"column"}}>
        <TextField
            required
            multiline
            value={data.title}
            sx={{width: "500px"}}
            margin="dense"
            id="title"
            name="title"
            label="Title"
            onChange={e => setData(prv => {
              return {
                ...prv,
                title: e.target.value
              }
            })}
            type="text"
            variant="standard"/>

        {/* Company field - now mandatory */}
        <FormControl sx={{width: "500px", mt: "8px"}}>
          <InputLabel id="company-select-label">Company *</InputLabel>
          <Select
              labelId="company-select-label"
              id="company-select"
              label="Company *"
              variant={"standard"}
              value={data.company || ""}
              onChange={e => setData(prv => {
                return {
                  ...prv,
                  company: e.target.value
                }
              })}
              required
          >
            {companies.map((company, i) => {
              return <MenuItem key={i} value={company}>{company.name}</MenuItem>
            })}
          </Select>
        </FormControl>

        {/* Format field - disabled until company is selected */}
        <FormControl sx={{width: "500px", mt: "16px"}}>
          <InputLabel id="format-select-label">Format</InputLabel>
          <Select
              labelId="format-select-label"
              id="format-select"
              label="Format"
              variant={"standard"}
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              disabled={!data.company || loadingFormats}
          >
            {formats.map((format, i) => {
              return <MenuItem key={i} value={format.key || format.value || format}>
                {format.label || format.name || format}</MenuItem>
            })}
          </Select>
        </FormControl>

        {/* New Subformat field - disabled until format is selected */}
        <FormControl sx={{width: "500px", mt: "16px"}}>
          <InputLabel id="subformat-select-label">Subformat</InputLabel>
          <Select
              labelId="subformat-select-label"
              id="subformat-select"
              label="Subformat"
              variant={"standard"}
              value={selectedSubformat}
              onChange={(e) => setSelectedSubformat(e.target.value)}
              disabled={!selectedFormat || loadingSubformats}
          >
            {subformats.map((subformat, i) => {
              return <MenuItem key={i} value={subformat.key || subformat.value || subformat}>
                {subformat.label || subformat.name || subformat}</MenuItem>
            })}
          </Select>
        </FormControl>

        <>
          <Button
              sx={{mt: "24px"}}
              variant="contained"
              component="label"
          >
            Upload Image
            <input
                type="file"
                hidden
                ref={inputFileRef}
                onChange={onFileChange}
            />
          </Button>

          {(imageTemplate.imageBase64) &&
              <Box
                  component="img"
                  sx={{
                    mt: "44px",
                    height: 150,
                    width: 150,
                  }}
                  src={imageTemplate.imageBase64}
              />}

          {type === "ARTICLE" && <>
            {imageTemplate.imageBase64 && <>
              <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                <TextField
                    required
                    sx={{width: "250px"}}
                    margin="dense"
                    id="color"
                    name="color"
                    label="color"
                    type="text"
                    variant="standard"
                    value={data.color}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        color: e.target.value
                      }
                    })}
                />
                <TextField
                    required
                    sx={{ml: "24px", width: "250px"}}
                    margin="dense"
                    id="fontSize"
                    name="fontSize"
                    label="font Size"
                    type="number"
                    variant="standard"
                    value={data.fontSize}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        fontSize: e.target.value
                      }
                    })}
                />
              </Box>
              <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                <TextField
                    required
                    sx={{width: "250px"}}
                    margin="dense"
                    id="x"
                    name="x"
                    label="x"
                    type="number"
                    variant="standard"
                    value={data.x}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        x: e.target.value
                      }
                    })}
                />
                <TextField
                    required
                    sx={{ml: "24px", width: "250px"}}
                    margin="dense"
                    id="y"
                    name="y"
                    label="y"
                    type="number"
                    variant="standard"
                    value={data.y}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        y: e.target.value
                      }
                    })}
                />
              </Box>
              <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                <TextField
                    required
                    sx={{width: "250px"}}
                    margin="dense"
                    id="maxWidth"
                    name="maxWidth"
                    label="maxWidth"
                    type="number"
                    variant="standard"
                    value={data.maxWidth}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        maxWidth: e.target.value
                      }
                    })}
                />
                <TextField
                    required
                    sx={{ml: "24px", width: "250px"}}
                    margin="dense"
                    id="maxHeight"
                    name="maxHeight"
                    label="maxHeight"
                    type="number"
                    variant="standard"
                    value={data.maxHeight}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        maxHeight: e.target.value
                      }
                    })}
                />
              </Box>
              <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Select
                    sx={{ml: "24px", width: "250px"}}
                    labelId="demo-simple-select-label"
                    id="font"
                    label="font"
                    value={selectedFont}
                    onChange={(e) => setSelectedFont(e.target.value)}
                >
                  {fonts.map((font, i) => {
                    return <MenuItem key={i} value={font}>{font}</MenuItem>
                  })}
                </Select>
                <TextField
                    required
                    sx={{ml: "24px", width: "250px"}}
                    margin="dense"
                    id="imageTitle"
                    name="imageTitle"
                    label="Image Title"
                    type="String"
                    variant="standard"
                    value={data.imageTitle}
                    onChange={e => setData(prv => {
                      return {
                        ...prv,
                        imageTitle: e.target.value
                      }})}
                />
              </Box>
            </>}
          </>}
        </>
        <Button sx={{margin: "24px"}}
                variant={"contained"}
                disabled={(data.title == null || data.title === "") || !data.company || !selectedFormat || !selectedSubformat}
                onClick={handleSubmit}
        >Generate</Button>
      </Box>
  )
};

export default ArticlePlayground;